import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form'; // Added Controller
import { TrashIcon, PlusIcon, ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, LinkIcon } from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Select from 'react-select';
import AsyncSelect from 'react-select/async'; // Import AsyncSelect
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO, isValid, addMonths, subDays, differenceInCalendarDays } from 'date-fns';
import { Link } from 'react-router-dom';

registerLocale('ru', ru);

// Updated Client interface (match GET /clients)
interface ClientOption {
  value: number;
  label: string; // e.g., "CompanyName (Contact Name)"
  clientData: Client;
}

interface Client {
  id: number;
  name: string; // Use name
  director?: string | null;
  currentDiscount?: number | null;
}

// Updated Structure interface (match GET /structures)
interface StructureOption {
  value: number;
  label: string; // e.g., "CODE - Type - Address"
  structureData: { // Store full structure data
    id: number;
    code: string;
    type: string;
    location: string;
    currentDailyPrice: number | null;
    currentMonthlyPrice: number | null;
  }
}

// Add StructureData type alias
type StructureData = StructureOption['structureData'];

// Updated QuoteItemFormData for submission
interface QuoteItemFormData {
  id?: number; // For existing items during update
  structureId: number | null; // Use null initially
  startDate: string;
  endDate: string;
  unitPrice?: number; // Optional override
  isMonthlyRate?: boolean | null; // Add this flag
  quantity?: number; // Add quantity for monthly rate
  discountPercentage?: number; // Optional override
  notes?: string;
  // --- Display Only Fields (not directly part of the form state for submission) ---
  _structureCode?: string; // For displaying selected structure info
  _structureType?: string;
  _calculatedPrice?: number; // Display calculated price from backend or estimate
  _calculatedTotal?: number; // Display calculated total from backend or estimate
}

// Updated QuoteFormData for submission
interface QuoteFormData {
  clientId: number | null; // Use null initially
  quoteNumber?: string; // Optional, backend might generate
  quoteDate: string;
  validUntil: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted'; // Add 'converted' status
  items: QuoteItemFormData[];
  notes?: string;
  termsAndConditions?: string;
  // Remove calculated fields: totalAmount, discountAmount, finalAmount
}

// --- Display only interfaces (populated after fetch in edit mode) ---
interface DisplayQuoteData extends QuoteFormData {
  totalAmount?: number;
  discountAmount?: number;
  finalAmount?: number;
}

type RateTypeValue = QuoteItemFormData['isMonthlyRate'] | string;

const normalizeRateType = (value: RateTypeValue | undefined): boolean | null => {
  if (value === 'monthly') return true;
  if (value === 'true') return true;
  if (value === 'daily') return false;
  if (value === 'false') return false;
  if (value === 'service') return null;
  if (value === null || value === false) return value;
  return true;
};

const rateTypeToSelectValue = (value: RateTypeValue | undefined): string => {
  const rateType = normalizeRateType(value);
  if (rateType === false) return 'daily';
  if (rateType === null) return 'service';
  return 'monthly';
};

interface EndDateUpdateOptions {
  startDate?: string | null;
  quantity?: number;
  rateType?: boolean | null;
}

// --- Availability Check ---
interface AvailabilityStatus {
  isChecking: boolean;
  isAvailable: boolean | null;
  message?: string | null;
  conflictingQuote?: { // Use conflictingQuote instead of conflictingOrder
    id: number;
    quoteNumber?: string;
    clientName?: string;
    status?: string;
    endDate?: string;
  } | null;
}

const QuoteForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useAuth(); // Changed from isAllowed
  const isEditMode = !!id;

  const queryParams = new URLSearchParams(location.search);
  const clientIdFromQuery = queryParams.get('clientId');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [structureOptions, setStructureOptions] = useState<StructureOption[]>([]);
  const [selectedClientOption, setSelectedClientOption] = useState<ClientOption | null>(null);
  const [displayQuoteData, setDisplayQuoteData] = useState<DisplayQuoteData | null>(null); // For displaying fetched totals
  const [itemAvailability, setItemAvailability] = useState<{ [itemId: string]: AvailabilityStatus }>({});

  // Form setup
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    getValues,
    trigger
  } = useForm<QuoteFormData>({
    defaultValues: {
      clientId: clientIdFromQuery ? Number(clientIdFromQuery) : null,
      quoteDate: new Date().toISOString().split('T')[0],
      validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 14 days validity
      status: 'draft',
      items: [],
      notes: '',
      termsAndConditions: ''
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  // Watch form values
  const watchClientId = watch('clientId');
  const watchItems = watch('items');
  const watchItemValues = watch('items'); // Watch individual item changes for calculation

  // Load initial data (Clients, Structures, Quote data if editing)
  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load available structures
        const structuresResponse = await axios.get('/structures');
        // Safely access the structures array, assuming it's in response.data.structures
        const structuresArray = structuresResponse.data && Array.isArray(structuresResponse.data.structures)
          ? structuresResponse.data.structures
          : [];
        const structureOpts = structuresArray.map((s: any) => ({
          value: s.id,
          label: `${s.code} - ${s.type} - ${s.location}`,
          structureData: { id: s.id, code: s.code, type: s.type, location: s.address, currentDailyPrice: s.currentDailyPrice, currentMonthlyPrice: s.currentMonthlyPrice }
        }));
        setStructureOptions(structureOpts);

        // If in edit mode, load quote data
        if (isEditMode) {
          const quoteResponse = await axios.get(`/quotes/${id}`);
          const quoteData = quoteResponse.data;
          setDisplayQuoteData(quoteData); // Store fetched data for display

          // Map API response to form data
          setValue('clientId', quoteData.clientId);
          setValue('quoteNumber', quoteData.quoteNumber); // Usually not editable, but set for context
          setValue('quoteDate', quoteData.quoteDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
          setValue('validUntil', quoteData.validUntil?.split('T')[0] || '');
          setValue('status', quoteData.status);
          setValue('notes', quoteData.notes || '');
          setValue('termsAndConditions', quoteData.termsAndConditions || '');

          // Pre-select client option for edit mode
          const editClientOpt = structureOpts.find((opt: StructureOption) => opt.value === quoteData.clientId);
          if (editClientOpt) setSelectedClientOption(editClientOpt);

          // Set items
          if (quoteData.QuoteItems && quoteData.QuoteItems.length > 0) {
            setValue('items', quoteData.QuoteItems.map((item: any) => ({
              id: item.id,
              structureId: item.structureId,
              startDate: item.startDate?.split('T')[0],
              endDate: item.endDate?.split('T')[0],
              unitPrice: item.unitPrice,
              isMonthlyRate: item.isMonthlyRate,
              discountPercentage: item.discountPercentage,
              notes: item.notes,
              // Populate display fields
              _structureCode: item.Structure?.code,
              _structureType: item.Structure?.type,
              _calculatedPrice: item.totalPrice,
              _calculatedTotal: item.finalPrice,
              quantity: item.quantity,
            } as QuoteItemFormData)));
          } else {
            setValue('items', []); // Ensure items is an empty array if no items from API
          }
        } else if (clientIdFromQuery) {
          // Fetch the initial client option based on query param
          try {
            const clientOptionRes = await axios.get(`/clients/options?search=&clientId=${clientIdFromQuery}`); // May need backend adjustment
            const foundOption = clientOptionRes.data.find((opt: ClientOption) => opt.value === Number(clientIdFromQuery));
            if (foundOption) {
              setSelectedClientOption(foundOption);
              setValue('clientId', Number(clientIdFromQuery));
            }
          } catch (clientErr) {
            console.error("Error fetching initial client option:", clientErr);
          }
        }
      } catch (err) {
        console.error('Error loading form data:', err);
        setError('Ошибка при загрузке данных для формы');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, clientIdFromQuery, setValue]); // Removed clientOptions dependency

  // --- Availability Check ---
  const handleCheckAvailability = useCallback(async (index: number) => {
    const item = getValues(`items.${index}`);
    // Use the unique ID generated by useFieldArray as the key
    const fieldArrayId = fields[index]?.id;

    if (!item || !item.structureId || !item.startDate || !item.endDate || !fieldArrayId) {
      setItemAvailability(prev => ({
        ...prev,
        [fieldArrayId]: { isChecking: false, isAvailable: null, message: null, conflictingQuote: null }
      }));
      return;
    }

    setItemAvailability(prev => ({ ...prev, [fieldArrayId]: { isChecking: true, isAvailable: null } }));

    try {
      const response = await axios.get('/orders/check-availability', {
        params: {
          structureId: item.structureId,
          startDate: item.startDate,
          endDate: item.endDate,
          excludeType: 'quote', // Check against orders mainly
          excludeId: isEditMode ? id : undefined // Exclude current quote if editing
        }
      });

      setItemAvailability(prev => ({
        ...prev,
        [fieldArrayId]: {
          isChecking: false,
          isAvailable: response.data.isAvailable,
          message: response.data.message, // Message might indicate conflict or warning
          conflictingQuote: response.data.conflictingOrder // Use conflictingOrder for now, adapt if needed
        }
      }));
    } catch (err: any) {
      console.error('Error checking availability:', err);
      setItemAvailability(prev => ({
        ...prev,
        [fieldArrayId]: {
          isChecking: false,
          isAvailable: null, // Indicate error or unknown status
          message: err.response?.data?.message || 'Ошибка проверки доступности'
        }
      }));
    }
  }, [getValues, fields, isEditMode, id]);

  // Trigger availability check when relevant fields change
  const triggerAvailabilityCheck = (index: number) => {
    handleCheckAvailability(index);
  };

  // Add a new item to the quote
  const handleAddItem = () => {
    const today = new Date();
    const nextMonthSameDay = addMonths(today, 1);
    const endDate = subDays(nextMonthSameDay, 1); // End date is one day before the start date next month

    append({
      structureId: null, // Start with null
      startDate: format(today, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'), // Default end date based on 1 month
      unitPrice: undefined, // Let backend/calculation handle default
      isMonthlyRate: true, // Default to monthly rate
      quantity: 1, // Default quantity 1 month
      discountPercentage: selectedClientOption?.clientData?.currentDiscount ?? 0, // Default discount from client
      notes: ''
    } as QuoteItemFormData);
  };

  // Handle form submission
  const onSubmit = async (formData: QuoteFormData) => {
    setSubmitting(true);
    setError(null);

    // Prepare data for backend (only necessary fields)
    const submissionData = {
      clientId: formData.clientId,
      notes: formData.notes,
      validUntil: formData.validUntil || null, // Send null if empty
      termsAndConditions: formData.termsAndConditions,
      status: formData.status, // Include status
      items: formData.items.map(item => {
        const isMonthlyRate = normalizeRateType(item.isMonthlyRate);

        return {
          id: isEditMode ? item.id : undefined,
          structureId: item.structureId,
          startDate: item.startDate,
          endDate: item.endDate, // Send calculated or manually entered end date
          unitPrice: item.unitPrice, // Send potentially user-overridden price
          isMonthlyRate,
          quantity: isMonthlyRate !== false ? item.quantity : undefined, // Only send quantity if monthly/service
          discountPercentage: item.discountPercentage,
          notes: item.notes
        };
      }).filter(item => item.structureId && item.startDate && item.endDate), // Basic validation filter
    };

    // Remove status if creating (backend should default to draft)
    if (!isEditMode) {
      delete (submissionData as any).status;
    }

    // Validate if client and items exist
    if (!submissionData.clientId) {
      setError('Пожалуйста, выберите клиента.');
      setSubmitting(false);
      return;
    }
    if (submissionData.items.length === 0) {
      setError('Пожалуйста, добавьте хотя бы одну конструкцию в предложение.');
      setSubmitting(false);
      return;
    }

    try {
      let response;
      if (isEditMode) {
        response = await axios.put(`/quotes/${id}`, submissionData);
      } else {
        response = await axios.post('/quotes', submissionData);
      }

      // Navigate to quote details page on success
      const quoteId = response.data.id || id;
      navigate(`/quotes/${quoteId}`);
    } catch (err: any) {
      console.error('Error saving quote:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении коммерческого предложения');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate display totals
  const [displayTotals, setDisplayTotals] = useState({ totalAmount: 0, discountAmount: 0, finalAmount: 0 });

  const calculateDisplayTotals = useCallback(() => {
    const items = getValues('items');
    let total = 0;
    let totalDiscount = 0;

    items.forEach(item => {
      const { unitPrice, discountPercentage = 0, startDate, endDate, quantity } = item;
      const isMonthlyRate = normalizeRateType(item.isMonthlyRate);
      if (unitPrice === undefined || unitPrice === null) return;

      let itemBasePrice = 0;
      
      if (isMonthlyRate === null) {
        itemBasePrice = unitPrice * (quantity || 1);
      }

      else if (isMonthlyRate === true) {
        itemBasePrice = unitPrice * (quantity || 0);
      }

      else {
        if (startDate && endDate) {
          try {
            const start = parseISO(startDate);
            const end = parseISO(endDate);
            if (isValid(start) && isValid(end)) {
              const durationDays = differenceInCalendarDays(end, start) + 1;
              if (durationDays < 1) return;
              itemBasePrice = unitPrice * durationDays;
            }
          } catch (e) { /* Handle error silently or log */ }
        }
      }

      const itemDiscount = itemBasePrice * (discountPercentage / 100);
      total += itemBasePrice;
      totalDiscount += itemDiscount;
    });

    setDisplayTotals({
      totalAmount: total,
      discountAmount: totalDiscount,
      finalAmount: total - totalDiscount,
    });
  }, [getValues]);

  // Recalculate totals when items change
  useEffect(() => {
    calculateDisplayTotals();
  }, [watchItemValues, calculateDisplayTotals]);

  // --- Helper Functions (Restored) ---

  // Function to load client options async
  const loadClientOptions = async (inputValue: string): Promise<ClientOption[]> => {
    try {
      const response = await axios.get(`/clients/options?search=${inputValue}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching client options:', error);
      return [];
    }
  };

  const handleClientChange = (option: ClientOption | null) => {
    setSelectedClientOption(option);
    setValue('clientId', option ? option.value : null, { shouldValidate: true });
  };

  // Calculate End Date based on Start Date and Quantity (Months)
  const updateCalculatedEndDate = (index: number, options: EndDateUpdateOptions = {}) => {
    const startDateStr = Object.prototype.hasOwnProperty.call(options, 'startDate')
      ? options.startDate
      : getValues(`items.${index}.startDate`);
    const quantityValue = Object.prototype.hasOwnProperty.call(options, 'quantity')
      ? options.quantity
      : getValues(`items.${index}.quantity`);
    const quantity = Number(quantityValue);
    const isMonthly = Object.prototype.hasOwnProperty.call(options, 'rateType')
      ? options.rateType
      : normalizeRateType(getValues(`items.${index}.isMonthlyRate`));

    if (isMonthly && startDateStr && quantity && quantity > 0) {
      try {
        const startDate = parseISO(startDateStr);
        // Add months, then subtract one day to get the end date of the period
        const endDate = subDays(addMonths(startDate, quantity), 1);
        setValue(`items.${index}.endDate`, format(endDate, 'yyyy-MM-dd'), { shouldDirty: true });
        trigger(`items.${index}.endDate`); // Trigger validation if needed
      } catch (e) {
        console.error("Error calculating end date:", e);
        setValue(`items.${index}.endDate`, '', { shouldDirty: true }); // Clear if invalid input
      }
    } else if (isMonthly && !startDateStr) {
      setValue(`items.${index}.endDate`, '', { shouldDirty: true });
    }
  };

  const validateItemEndDate = (index: number, value: string | null | undefined, isRequired: boolean) => {
    if (!isRequired) return true;

    const startDateStr = getValues(`items.${index}.startDate`);
    if (!startDateStr || !value) return 'Дата конца обязательна';

    try {
      const startDate = parseISO(startDateStr);
      const endDate = parseISO(value);
      return (
        isValid(startDate) &&
        isValid(endDate) &&
        differenceInCalendarDays(endDate, startDate) >= 0
      ) || 'Конец не раньше начала';
    } catch {
      return 'Неверный формат даты';
    }
  };

  // Handle changes in Structure, Rate Type, Dates, Quantity, Price, Discount
  const handleItemChange = (index: number) => {
    // Recalculate totals whenever an item potentially changes its price components
    calculateDisplayTotals();
  };

  // Calculate unit price based on structure and rate type
  const calculateUnitPrice = (structureData: StructureData | undefined, isMonthly: boolean, isService: boolean): number | undefined => {
    if (!structureData) return undefined;
    
    if (isService) {
      return structureData.currentMonthlyPrice ?? undefined;
    }
    
    return isMonthly ? (structureData.currentMonthlyPrice ?? undefined) : (structureData.currentDailyPrice ?? undefined);
  };

  // Calculate final price for a single item
  const calculateItemFinalPrice = (item: QuoteItemFormData | undefined): number => {
    if (!item) return 0;

    const { unitPrice, discountPercentage = 0, startDate, endDate, quantity } = item;
    const isMonthlyRate = normalizeRateType(item.isMonthlyRate);

    if (unitPrice === undefined || unitPrice === null) return 0;

    let basePrice = 0;
    
    if (isMonthlyRate === null) {
      basePrice = unitPrice * (quantity || 1);
    } 
    else if (isMonthlyRate === true) {
      basePrice = unitPrice * (quantity || 0);
    } 
    else {
      if (startDate && endDate) {
        try {
          const start = parseISO(startDate);
          const end = parseISO(endDate);
          if (isValid(start) && isValid(end)) {
            const durationDays = differenceInCalendarDays(end, start) + 1;
            if (durationDays < 1) return 0;
            basePrice = unitPrice * durationDays;
          }
        } catch (e) {
          console.error("Error parsing dates for daily calculation:", e);
          return 0;
        }
      }
    }

    const discount = basePrice * (discountPercentage / 100);
    return basePrice - discount;
  };

  // Check permissions
  if (isEditMode ? !can('quotes.edit') : !can('quotes.create')) { // Changed from isAllowed
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к этой странице</p>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? `Редактировать КП #${displayQuoteData?.quoteNumber || id}` : 'Создать коммерческое предложение'}
        backLink="/quotes"
      />

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Основная информация</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Client Selection */}
            <div className="md:col-span-1">
              <label htmlFor="clientId" className="form-label">Клиент *</label>
              <Controller
                name="clientId"
                control={control}
                rules={{ required: 'Клиент обязателен' }}
                render={({ field, fieldState: { error } }) => (
                  <AsyncSelect<ClientOption>
                    inputId="clientId"
                    cacheOptions
                    defaultOptions // Load initial options eagerly
                    loadOptions={loadClientOptions}
                    value={selectedClientOption}
                    onChange={(option) => {
                      handleClientChange(option);
                      // Update field value for react-hook-form
                      field.onChange(option ? option.value : null);
                    }}
                    placeholder="Поиск клиента..."
                    noOptionsMessage={() => 'Клиент не найден'}
                    classNamePrefix="react-select"
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    menuPortalTarget={document.body}
                  />
                )}
              />
              {errors.clientId && <p className="text-red-500 text-xs mt-1">{errors.clientId.message}</p>}
            </div>

            {/* Quote Number (Display Only if Edit) */}
            {isEditMode && displayQuoteData?.quoteNumber && (
              <div className="md:col-span-1">
                <label htmlFor="quoteNumberDisplay" className="form-label">Номер КП</label>
                <input
                  id="quoteNumberDisplay"
                  type="text"
                  className="form-input bg-gray-100 cursor-not-allowed"
                  value={displayQuoteData.quoteNumber}
                  readOnly
                />
              </div>
            )}

            {/* Quote Date */}
            <div className="md:col-span-1">
              <label htmlFor="quoteDate" className="form-label">Дата КП *</label>
              <input
                id="quoteDate"
                type="date"
                className={`form-input ${errors.quoteDate ? 'border-red-500' : ''}`}
                {...register('quoteDate', { required: 'Дата КП обязательна' })}
              />
              {errors.quoteDate && <p className="text-red-500 text-xs mt-1">{errors.quoteDate.message}</p>}
            </div>

            {/* Valid Until Date */}
            <div className="md:col-span-1">
              <label htmlFor="validUntil" className="form-label">Действительно до *</label>
              <input
                id="validUntil"
                type="date"
                className={`form-input ${errors.validUntil ? 'border-red-500' : ''}`}
                {...register('validUntil', { required: 'Срок действия обязателен' })}
              />
              {errors.validUntil && <p className="text-red-500 text-xs mt-1">{errors.validUntil.message}</p>}
            </div>

            {/* Status (Only if editing) */}
            {isEditMode && (
              <div className="md:col-span-1">
                <label htmlFor="status" className="form-label">Статус</label>
                <select
                  id="status"
                  className="form-input"
                  {...register('status')}
                >
                  <option value="draft">Черновик</option>
                  <option value="sent">Отправлено</option>
                  <option value="accepted">Принято</option>
                  <option value="rejected">Отклонено</option>
                  <option value="expired">Истек срок</option>
                  <option value="converted">Конвертировано</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Quote Items Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Позиции предложения</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Конструкция</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Начало</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Период/Кол-во</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Цена ед. / Тариф</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Скидка %</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Итого</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заметки</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[50px]"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((field, index) => {
                  return (
                    <tr key={field.id}>
                      {/* Structure Selection */}
                      <td className="px-3 py-2 align-top relative">
                        <Controller
                          name={`items.${index}.structureId`}
                          control={control}
                          rules={{ required: 'Выберите конструкцию' }}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Select<StructureOption>
                              inputId={`items.${index}.structureId`}
                              options={structureOptions}
                              value={structureOptions.find(opt => opt.value === value) || null}
                              onChange={(option: StructureOption | null) => {
                                onChange(option ? option.value : null);
                                const structureData = option?.structureData;
                                const currentIsMonthly = getValues(`items.${index}.isMonthlyRate`);
                                const rateType = currentIsMonthly === undefined ? true : normalizeRateType(currentIsMonthly);
                                const newUnitPrice = calculateUnitPrice(structureData, rateType === true, rateType === null);

                                setValue(`items.${index}.unitPrice`, newUnitPrice, { shouldDirty: true });
                                setValue(`items.${index}.isMonthlyRate`, rateType, { shouldDirty: true }); // Ensure rate type is set
                                // Discount can be set here based on client or default
                                const clientDiscount = selectedClientOption?.clientData?.currentDiscount ?? 0;
                                setValue(`items.${index}.discountPercentage`, clientDiscount, { shouldDirty: true });

                                trigger(`items.${index}.unitPrice`);
                                trigger(`items.${index}.discountPercentage`);
                                handleItemChange(index); // Recalculate totals
                                handleCheckAvailability(index); // Check availability
                              }}
                              placeholder="Выберите..."
                              classNamePrefix="react-select-item"
                              noOptionsMessage={() => 'Конструкции не найдены'}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 }),
                                control: (base: any) => ({ ...base, minHeight: '38px', height: '38px', fontSize: '0.875rem' }),
                                valueContainer: (base: any) => ({ ...base, height: '38px', padding: '0 6px' }),
                                indicatorsContainer: (base: any) => ({ ...base, height: '38px' })
                              }}
                            />
                          )}
                        />
                        {errors.items?.[index]?.structureId && (
                          <p className="text-red-500 text-xs mt-1">{errors.items[index]?.structureId?.message}</p>
                        )}
                        {/* Availability Status Display */}
                        <div className="text-xs mt-1 space-y-0.5">
                          <div className="flex items-center space-x-1">
                            {itemAvailability[field.id]?.isChecking && (
                              <><ArrowPathIcon className="h-3 w-3 text-gray-400 animate-spin flex-shrink-0" /><span className="text-gray-500">Проверка...</span></>
                            )}
                            {/* Show available status differently for quotes (optional) */}
                            {itemAvailability[field.id]?.isAvailable === true && (
                              <><CheckCircleIcon className="h-3 w-3 text-green-500 flex-shrink-0" /><span className="text-green-600">Доступно в этот период</span></>
                            )}
                            {/* Show conflict as a warning for quotes */}
                            {itemAvailability[field.id]?.isAvailable === false && (
                              <>
                                <ExclamationTriangleIcon className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                                <span className="text-yellow-600">
                                  {itemAvailability[field.id]?.message || 'Занято другим заказом'}
                                </span>
                                {itemAvailability[field.id]?.conflictingQuote && (
                                  <Link
                                    to={`/orders/${itemAvailability[field.id]?.conflictingQuote?.id}`} // Link to order for now
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Конфликт с Заказом #${itemAvailability[field.id]?.conflictingQuote?.quoteNumber || itemAvailability[field.id]?.conflictingQuote?.id}`}
                                    className="ml-1 text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0"
                                  >
                                    <LinkIcon className="h-3 w-3" />
                                  </Link>
                                )}
                              </>
                            )}
                            {/* Show other messages/errors */}
                            {itemAvailability[field.id]?.isAvailable === null && !itemAvailability[field.id]?.isChecking && itemAvailability[field.id]?.message && (
                              <><ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" /><span className="text-red-600">{itemAvailability[field.id]?.message}</span></>
                            )}
                          </div>
                          {itemAvailability[field.id]?.isAvailable === false && itemAvailability[field.id]?.conflictingQuote && (
                            <div className="pl-4 text-gray-500 text-xs border-l-2 border-yellow-300 ml-1 mt-1 pt-0.5">
                              <span>Заказ: <span className="font-medium">#{itemAvailability[field.id]?.conflictingQuote?.quoteNumber || itemAvailability[field.id]?.conflictingQuote?.id}</span></span><br />
                              <span>Клиент: <span className="font-medium">{itemAvailability[field.id]?.conflictingQuote?.clientName || '-'}</span></span><br />
                              <span>Освободится: <span className="font-medium">{itemAvailability[field.id]?.conflictingQuote?.endDate ? format(parseISO(itemAvailability[field.id]!.conflictingQuote!.endDate!), 'dd.MM.yyyy') : '-'}</span></span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Start Date */}
                      <td className="px-3 py-2 align-top">
                        <Controller
                          control={control}
                          name={`items.${index}.startDate`}
                          rules={{ required: "Дата обязательна" }}
                          render={({ field, fieldState: { error } }) => (
                            <DatePicker
                              selected={field.value ? parseISO(field.value) : null}
                              onChange={(date) => {
                                const newDate = date ? format(date, 'yyyy-MM-dd') : null;
                                field.onChange(newDate);
                                trigger(`items.${index}.endDate`); // Trigger validation related to end date
                                updateCalculatedEndDate(index, { startDate: newDate }); // Recalculate end date if monthly
                                handleItemChange(index); // Recalculate totals
                                triggerAvailabilityCheck(index); // Check availability
                              }}
                              dateFormat="dd.MM.yyyy"
                              locale={ru}
                              className={`form-input w-full ${error ? 'border-red-500' : ''}`}
                              placeholderText="ДД.ММ.ГГГГ"
                              popperPlacement="top-start"
                              popperClassName="z-[1000]"
                            />
                          )}
                        />
                        {errors.items?.[index]?.startDate && <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.startDate?.message}</p>}
                      </td>

                      {/* Period/Quantity */}
                      <td className="px-3 py-2 align-top">
                        {watchItems[index]?.isMonthlyRate === true ? (
                          // Monthly: Quantity input + Calculated End Date display
                          <div className="space-y-1">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className={`form-input w-full ${errors.items?.[index]?.quantity ? 'border-red-500' : ''}`}
                              placeholder="Кол-во мес."
                              {...register(`items.${index}.quantity` as const, {
                                required: watchItems[index]?.isMonthlyRate === true ? 'Кол-во мес. обязательно' : false,
                                min: { value: 1, message: 'Мин. 1 месяц' },
                                valueAsNumber: true,
                                onChange: (e) => {
                                  updateCalculatedEndDate(index, { quantity: Number(e.target.value) });
                                  handleItemChange(index); // Recalculate totals
                                  triggerAvailabilityCheck(index); // Check availability
                                }
                              })}
                            />
                            {errors.items?.[index]?.quantity && (
                              <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.quantity?.message}</p>
                            )}
                            <Controller
                              control={control}
                              name={`items.${index}.endDate`}
                              rules={{
                                required: watchItems[index]?.isMonthlyRate === true ? 'Дата конца обязательна' : false,
                                validate: value => validateItemEndDate(index, value, watchItems[index]?.isMonthlyRate === true)
                              }}
                              render={({ field: endDateField, fieldState: { error: endDateError } }) => (
                                <DatePicker
                                  selected={endDateField.value ? parseISO(endDateField.value) : null}
                                  onChange={(date) => {
                                    const newDate = date ? format(date, 'yyyy-MM-dd') : null;
                                    endDateField.onChange(newDate);
                                    trigger(`items.${index}.startDate`);
                                    handleItemChange(index);
                                    triggerAvailabilityCheck(index);
                                  }}
                                  minDate={watchItems?.[index]?.startDate ? parseISO(watchItems[index].startDate) : undefined}
                                  dateFormat="dd.MM.yyyy"
                                  locale={ru}
                                  className={`form-input w-full text-sm ${endDateError ? 'border-red-500' : ''}`}
                                  placeholderText="Дата конца"
                                  popperPlacement="top-start"
                                  popperClassName="z-[1000]"
                                />
                              )}
                            />
                            {errors.items?.[index]?.endDate && (
                              <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.endDate?.message}</p>
                            )}
                          </div>
                        ) : watchItems[index]?.isMonthlyRate === null ? (
                          <div>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className={`form-input w-full ${errors.items?.[index]?.quantity ? 'border-red-500' : ''}`}
                              placeholder="Кол-во ед."
                              {...register(`items.${index}.quantity` as const, {
                                required: watchItems[index]?.isMonthlyRate === null ? 'Кол-во обязательно' : false,
                                min: { value: 1, message: 'Мин. 1' },
                                valueAsNumber: true,
                                onChange: () => handleItemChange(index)
                              })}
                            />
                            {errors.items?.[index]?.quantity && (
                              <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.quantity?.message}</p>
                            )}
                          </div>
                        ) : (
                          // Daily: End Date input
                          <>
                            <Controller
                              control={control}
                              name={`items.${index}.endDate`}
                              rules={{
                                required: watchItems[index]?.isMonthlyRate === false ? 'Дата конца обязательна' : false,
                                validate: value => validateItemEndDate(index, value, watchItems[index]?.isMonthlyRate === false)
                              }}
                              render={({ field: endDateField, fieldState: { error: endDateError } }) => (
                                <DatePicker
                                  selected={endDateField.value ? parseISO(endDateField.value) : null}
                                  onChange={(date) => {
                                    const newDate = date ? format(date, 'yyyy-MM-dd') : null;
                                    endDateField.onChange(newDate);
                                    trigger(`items.${index}.startDate`); // Trigger dependent validation
                                    handleItemChange(index); // Recalculate totals
                                    triggerAvailabilityCheck(index); // Check availability
                                  }}
                                  minDate={watchItems?.[index]?.startDate ? parseISO(watchItems[index].startDate) : undefined}
                                  dateFormat="dd.MM.yyyy"
                                  locale={ru}
                                  className={`form-input w-full ${endDateError ? 'border-red-500' : ''}`}
                                  placeholderText={"ДД.ММ.ГГГГ"}
                                  popperPlacement="top-start"
                                  popperClassName="z-[1000]"
                                />
                              )}
                            />
                            {errors.items?.[index]?.endDate && (
                              <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.endDate?.message}</p>
                            )}
                          </>
                        )}
                      </td>

                      {/* Unit Price / Rate Type */}
                      <td className="px-3 py-2 align-top text-right">
                        <div className="flex flex-col items-end">
                          <input
                            type="number"
                            step="0.01"
                            className={`form-input text-right w-full ${errors.items?.[index]?.unitPrice ? 'border-red-500' : ''}`}
                            {...register(`items.${index}.unitPrice` as const, {
                              valueAsNumber: true,
                              required: 'Цена обязательна',
                              min: { value: 0, message: 'Цена >= 0' },
                              onChange: () => handleItemChange(index)
                            })}
                            placeholder="Цена"
                          />
                          <Controller
                            control={control}
                            name={`items.${index}.isMonthlyRate`}
                            render={({ field: rateField }) => (
                              <select
                                className="form-input text-xs w-full mt-1"
                                value={rateTypeToSelectValue(rateField.value)}
                                onChange={(e) => {
                                  const rateType = normalizeRateType(e.target.value);
                                  const isMonthlyNow = rateType === true;
                                  const isServiceNow = rateType === null;

                                  rateField.onChange(rateType);

                                  if (isMonthlyNow) {
                                    setValue(`items.${index}.endDate`, '', { shouldValidate: false });
                                    setValue(`items.${index}.quantity`, 1, { shouldDirty: true });
                                    updateCalculatedEndDate(index, { quantity: 1, rateType });
                                  } else if (isServiceNow) {
                                    setValue(`items.${index}.endDate`, getValues(`items.${index}.startDate`), { shouldValidate: false });
                                    setValue(`items.${index}.quantity`, 1, { shouldDirty: true });
                                  } else {
                                    const startDate = getValues(`items.${index}.startDate`);
                                    let defaultEndDate = '';
                                    if (startDate) {
                                      const thirtyDaysLater = new Date(parseISO(startDate));
                                      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
                                      defaultEndDate = format(thirtyDaysLater, 'yyyy-MM-dd');
                                    }
                                    setValue(`items.${index}.endDate`, defaultEndDate, { shouldValidate: true });
                                    if (defaultEndDate) triggerAvailabilityCheck(index);
                                  }

                                  const currentStructureId = getValues(`items.${index}.structureId`);
                                  const structureOption = structureOptions.find(opt => opt.value === currentStructureId);
                                  const unitPrice = calculateUnitPrice(structureOption?.structureData, isMonthlyNow, isServiceNow);
                                  setValue(`items.${index}.unitPrice`, unitPrice, { shouldDirty: true });
                                  trigger(isMonthlyNow || isServiceNow ? `items.${index}.quantity` : `items.${index}.endDate`);
                                  handleItemChange(index);
                                  triggerAvailabilityCheck(index);
                                }}
                              >
                                <option value="monthly">в месяц</option>
                                <option value="daily">в день</option>
                                <option value="service">услуга</option>
                              </select>
                            )}
                          />
                          {errors.items?.[index]?.unitPrice && <p className="text-red-600 text-xs mt-1">{errors.items?.[index]?.unitPrice?.message}</p>}
                        </div>
                      </td>

                      {/* Discount */}
                      <td className="px-3 py-2 align-top text-right">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-input text-right w-full ${errors.items?.[index]?.discountPercentage ? 'border-red-500' : ''}`}
                          {...register(`items.${index}.discountPercentage` as const, {
                            valueAsNumber: true,
                            min: { value: 0, message: 'Скидка >= 0' },
                            max: { value: 100, message: 'Скидка <= 100' },
                            onChange: () => handleItemChange(index) // Recalculate totals on change
                          })}
                          placeholder="Скидка %"
                        />
                        {errors.items?.[index]?.discountPercentage && <p className="text-red-600 text-xs mt-1">{errors.items?.[index]?.discountPercentage?.message}</p>}
                      </td>

                      {/* Item Total */}
                      <td className="px-3 py-2 align-top text-right text-sm text-gray-700 font-medium">
                        {formatCurrency(calculateItemFinalPrice(watchItems[index]))}
                      </td>

                      {/* Notes */}
                      <td className="px-3 py-2 align-top">
                        <textarea
                          rows={1}
                          className="form-input text-xs w-full"
                          placeholder="Заметка..."
                          {...register(`items.${index}.notes` as const)}
                        />
                      </td>

                      {/* Actions (Remove Button) */}
                      <td className="px-3 py-2 align-top text-center">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100"
                          title="Удалить позицию"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  {/* Adjust colspan based on the number of columns */}
                  <td colSpan={5} className="px-3 py-3 text-right text-sm font-medium text-gray-700 uppercase">
                    Итого по позициям:
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-semibold text-gray-900">
                    {formatCurrency(displayTotals?.finalAmount)}
                  </td>
                  {/* Adjust colspan based on the number of columns */}
                  <td colSpan={2} className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {/* Add Item Button */}
          <div className="mt-4">
            <button
              type="button"
              onClick={handleAddItem}
              className="btn-secondary inline-flex items-center text-sm"
              disabled={!watchClientId} // Disable if no client is selected
              title={!watchClientId ? "Сначала выберите клиента" : "Добавить позицию"}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Добавить позицию
            </button>
            {!watchClientId && <p className="text-xs text-gray-500 mt-1">Сначала выберите клиента, чтобы добавить позицию.</p>}
          </div>
        </div>

        {/* Additional Options (Notes, T&C) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white rounded-lg shadow p-6 space-y-4">
            <div>
              <label htmlFor="notes" className="form-label">Заметки (Бренд и т.п.)</label>
              <textarea id="notes" rows={3} className="form-input" {...register('notes')}></textarea>
            </div>
            <div>
              <label htmlFor="termsAndConditions" className="form-label">Условия и положения</label>
              <textarea id="termsAndConditions" rows={3} className="form-input" {...register('termsAndConditions')}></textarea>
            </div>
          </div>
          <div className="md:col-span-1 bg-white rounded-lg shadow p-6 flex flex-col justify-between">
            <h3 className="text-lg font-semibold mb-4">Итоги</h3>
            <div className="space-y-2 text-right">
              {/* Display calculated totals */}
              <div className="flex justify-between"><span className="text-gray-600">Сумма:</span> <span className="font-medium">{formatCurrency(displayTotals.totalAmount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Скидка:</span> <span className="font-medium">{formatCurrency(displayTotals.discountAmount)}</span></div>
              <hr className="my-2" />
              <div className="flex justify-between text-xl font-bold"><span className="text-gray-800">Итого:</span> <span className="text-primary">{formatCurrency(displayTotals.finalAmount)}</span></div>
            </div>
          </div>
        </div>

        {/* Totals Section */}
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-xl font-bold mb-4">Итоги предложения</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="totalAmountDisplay" className="form-label">Общая стоимость</label>
              <input
                id="totalAmountDisplay"
                type="text"
                className="form-input bg-gray-100 cursor-not-allowed"
                value={formatCurrency(displayTotals.totalAmount)}
                readOnly
                tabIndex={-1}
              />
            </div>

            <div>
              <label htmlFor="discountAmountDisplay" className="form-label">Скидка</label>
              <input
                id="discountAmountDisplay"
                type="text"
                className="form-input bg-gray-100 cursor-not-allowed"
                value={formatCurrency(displayTotals.discountAmount)}
                readOnly
                tabIndex={-1}
              />
            </div>

            <div>
              <label htmlFor="finalAmountDisplay" className="form-label">Итого к оплате</label>
              <input
                id="finalAmountDisplay"
                type="text"
                className="form-input bg-gray-100 cursor-not-allowed text-lg font-semibold text-primary" // Highlight final amount
                value={formatCurrency(displayTotals.finalAmount)}
                readOnly
                tabIndex={-1}
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="pt-5 flex justify-end space-x-3">
          <button type="button" onClick={() => navigate('/quotes')} className="btn-secondary" disabled={submitting}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить изменения' : 'Создать КП')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuoteForm;
